/**
 * MasterDefaults — Settings template applied ONCE on dealer creation.
 *
 * D4 spec: No retro template push. Applied only at creation time.
 * Route: /super-admin/master-defaults
 *
 * REQ-SA-03: Configure the seed schema (mirrors src/data/seedDefaults.json)
 * that new dealers inherit at creation. Persisted to the IDI tenant settings
 * record under the `masterDefaults` key via superAdminAPI.updateMasterDefaults.
 */

import React, { useEffect, useState } from 'react';
import PageHeader from '../ui/PageHeader';
import { Button, useToast } from '../ui';
import { superAdminAPI } from '../../services/api';
// PR-B2b-part-2A (2026-07-21): seedDefaults.json is now a generated artifact
// (regenerated at build time from constants/vocabSeeds.js via
// scripts/export-vocab-seeds.js). It carries a `$schema` metadata key that
// MasterDefaults should NOT save into the tenant `masterDefaults` record. We
// import and strip it here so downstream state + save calls never see it.
import seedDefaultsRaw from '../../data/seedDefaults.json';
const { $schema: _seedDefaultsSchema, ...seedDefaults } = seedDefaultsRaw;

// Simple editable list of { name, active } entries.
function NameList({ label, items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const update = (i, patch) => {
    const next = list.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const add = () => onChange([...list, { name: '', active: true }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
          {label}
        </label>
        <Button variant="secondary" size="sm" onClick={add}>
          Add
        </Button>
      </div>
      {list.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          No entries.
        </p>
      )}
      {list.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            className="flex-1 rounded border px-2 py-1 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
            value={it.name || ''}
            placeholder="Name"
            onChange={(e) => update(i, { name: e.target.value })}
          />
          <label className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            <input
              type="checkbox"
              checked={it.active !== false}
              style={{ accentColor: 'var(--color-action-primary)' }}
              onChange={(e) => update(i, { active: e.target.checked })}
            />
            Active
          </label>
          <button
            type="button"
            className="text-xs"
            style={{ color: 'var(--color-status-error)' }}
            onClick={() => remove(i)}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

export default function MasterDefaults() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(seedDefaults);
  // IDI-65 §5: if the initial load fails we must NOT let the user Save — the
  // in-state `defaults` is still the seed template, and saving it would
  // overwrite the tenant's real master defaults with seed data.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadFailed(false);
        const saved = await superAdminAPI.getMasterDefaults();
        if (alive && saved && Object.keys(saved).length > 0) {
          // Merge saved over the seed schema so any newly-added keys are present.
          setDefaults({ ...seedDefaults, ...saved });
        }
      } catch (e) {
        // IDI-65: the load actually failed. Do NOT silently keep the seed as if
        // it were real — flag it so Save is disabled and a banner is shown.
        if (alive) setLoadFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const set = (key, value) => setDefaults((d) => ({ ...d, [key]: value }));
  const setMargin = (k, v) =>
    setDefaults((d) => ({
      ...d,
      marginDefaults: { ...(d.marginDefaults || {}), [k]: v },
    }));

  const save = async () => {
    // IDI-65 §5: never write the seed over real data when the load failed.
    if (loadFailed) {
      toast.error('Cannot save — master defaults failed to load', {
        description: 'Saving now would overwrite real data with the seed template. Reload and try again.',
      });
      return;
    }
    setSaving(true);
    try {
      await superAdminAPI.updateMasterDefaults(defaults);
      toast.success('Master defaults saved');
    } catch (err) {
      toast.error('Failed to save master defaults', { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const margins = defaults.marginDefaults || {};
  const quoteValidity =
    (defaults.quoteDefaults && defaults.quoteDefaults.validityDays) ?? 30;
  const taxRate =
    Array.isArray(defaults.taxRates) && defaults.taxRates[0]
      ? defaults.taxRates[0].rate
      : 0.07;

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Master Defaults" subtitle="Loading…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Master Defaults"
        subtitle="Settings template applied to new contractors at creation time"
        actions={
          <Button variant="primary" onClick={save} disabled={saving || loadFailed}>
            {saving ? 'Saving…' : 'Save Defaults'}
          </Button>
        }
      />

      <div
        className="card p-4 text-sm"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
      >
        These defaults are applied <strong>once</strong> when a new contractor account is created.
        Changes here do <strong>not</strong> retroactively affect existing contractors.
      </div>

      {loadFailed && (
        <div
          className="card p-4 text-sm"
          style={{ borderColor: 'var(--color-error)', background: 'var(--color-error-bg)', color: 'var(--color-status-error-text)' }}
          role="alert"
        >
          <strong>Couldn't load the saved master defaults.</strong> The values below are the seed
          template, not this account's real settings. Saving is disabled to avoid overwriting real
          data — reload the page to try again.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div
          className="card p-4"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <NameList
            label="Job Types"
            items={defaults.jobTypes}
            onChange={(v) => set('jobTypes', v)}
          />
        </div>
        <div
          className="card p-4"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <NameList
            label="Lead Sources"
            items={defaults.leadSources}
            onChange={(v) => set('leadSources', v)}
          />
        </div>
        <div
          className="card p-4"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <NameList
            label="Customer Types"
            items={defaults.customerTypes}
            onChange={(v) => set('customerTypes', v)}
          />
        </div>

        <div
          className="card p-4 space-y-4"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <div>
            <label className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
              Default Tax Rate (%)
            </label>
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full rounded border px-2 py-1 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
              value={Number((taxRate * 100).toFixed(2))}
              onChange={(e) => {
                const pct = parseFloat(e.target.value);
                const rate = Number.isFinite(pct) ? pct / 100 : 0;
                const existing =
                  Array.isArray(defaults.taxRates) && defaults.taxRates[0]
                    ? defaults.taxRates[0]
                    : { name: 'Default Tax', active: true };
                set('taxRates', [{ ...existing, rate }]);
              }}
            />
          </div>

          <div>
            <label className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
              Quote Validity (days)
            </label>
            <input
              type="number"
              className="mt-1 w-full rounded border px-2 py-1 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
              value={quoteValidity}
              onChange={(e) => {
                const days = parseInt(e.target.value, 10);
                set('quoteDefaults', {
                  ...(defaults.quoteDefaults || {}),
                  validityDays: Number.isFinite(days) ? days : 30,
                });
              }}
            />
          </div>
        </div>

        <div
          className="card p-4 space-y-3"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <label className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
            Default Margins (%)
          </label>
          {['material', 'labor', 'overhead'].map((k) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-24 text-sm capitalize" style={{ color: 'var(--color-text-secondary)' }}>
                {k}
              </span>
              <input
                type="number"
                step="1"
                className="flex-1 rounded border px-2 py-1 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
                value={Number((((margins[k] ?? 0) * 100)).toFixed(0))}
                onChange={(e) => {
                  const pct = parseFloat(e.target.value);
                  setMargin(k, Number.isFinite(pct) ? pct / 100 : 0);
                }}
              />
            </div>
          ))}
        </div>

        <div
          className="card p-4 lg:col-span-2"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <label className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
            Default Terms &amp; Conditions
          </label>
          <textarea
            rows={5}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
            value={defaults.tcTemplate || ''}
            onChange={(e) => set('tcTemplate', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
